// Comprehensive Scoring Service for Strategic Planning Platform
// Handles all scoring logic consistently across Foundation, External Assessment, Internal Assessment, and Strategy Formulation

export interface ScoreResult {
  score: number;
  level: string;
  color: string;
  description: string;
}

export interface ProgressResult {
  completed: number;
  total: number;
  percentage: number;
  status: 'not_started' | 'in_progress' | 'completed';
}

// =============================================================================
// FOUNDATION SCORING
// =============================================================================

/**
 * Calculate quality score for foundation elements (Vision, Mission, Values)
 * Based on checklist completion percentage
 */
export const calculateFoundationQuality = (
  checkedCount: number,
  totalQuestions: number
): ScoreResult => {
  if (totalQuestions === 0) {
    return {
      score: 0,
      level: 'Not Assessed',
      color: 'bg-gray-100 text-gray-600',
      description: 'No assessment completed',
    };
  }

  const percentage = Math.round((checkedCount / totalQuestions) * 100);

  if (percentage >= 80) {
    return {
      score: percentage,
      level: 'Excellent',
      color: 'bg-green-100 text-green-800',
      description: 'High quality foundation element',
    };
  } else if (percentage >= 60) {
    return {
      score: percentage,
      level: 'Good',
      color: 'bg-blue-100 text-blue-800',
      description: 'Solid foundation element',
    };
  } else if (percentage >= 40) {
    return {
      score: percentage,
      level: 'Fair',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Needs improvement',
    };
  } else if (percentage >= 20) {
    return {
      score: percentage,
      level: 'Poor',
      color: 'bg-orange-100 text-orange-800',
      description: 'Significant improvement needed',
    };
  } else {
    return {
      score: percentage,
      level: 'Very Poor',
      color: 'bg-red-100 text-red-800',
      description: 'Major revision required',
    };
  }
};

/**
 * Calculate objectives completion by category
 */
export const calculateObjectivesProgress = (objectives: any[]): ProgressResult => {
  const total = objectives.length;
  const completed = objectives.filter((obj) => obj.goal && obj.measure && obj.targetDate).length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    completed,
    total,
    percentage,
    status: percentage === 100 ? 'completed' : percentage > 0 ? 'in_progress' : 'not_started',
  };
};

// =============================================================================
// EXTERNAL ASSESSMENT SCORING
// =============================================================================

/**
 * Calculate PESTEL challenge level
 * Reverse logic: High opportunities = Low challenge (good for company)
 */
export const calculatePestelChallenge = (opportunityPercentage: number): ScoreResult => {
  if (opportunityPercentage < 0) {
    return {
      score: -1,
      level: 'Not Analyzed',
      color: 'bg-gray-100 text-gray-600',
      description: 'Analysis not completed',
    };
  }

  // Reverse logic: High opportunities = Low challenge
  if (opportunityPercentage >= 80) {
    return {
      score: opportunityPercentage,
      level: 'Very Low',
      color: 'bg-blue-100 text-blue-800',
      description: 'Highly favorable external environment',
    };
  } else if (opportunityPercentage >= 60) {
    return {
      score: opportunityPercentage,
      level: 'Low',
      color: 'bg-green-100 text-green-800',
      description: 'Favorable external environment',
    };
  } else if (opportunityPercentage >= 40) {
    return {
      score: opportunityPercentage,
      level: 'Moderate',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Mixed external environment',
    };
  } else if (opportunityPercentage >= 20) {
    return {
      score: opportunityPercentage,
      level: 'High',
      color: 'bg-orange-100 text-orange-800',
      description: 'Challenging external environment',
    };
  } else {
    return {
      score: opportunityPercentage,
      level: 'Very High',
      color: 'bg-red-100 text-red-800',
      description: 'Very challenging external environment',
    };
  }
};

/**
 * Calculate Five Forces pressure level and industry attractiveness
 * Uses exact logic from external assessment
 */
export const calculateFiveForcesPressure = (
  strongFactorsChecked: number,
  totalStrongFactors: number,
  weakFactorsChecked: number,
  totalWeakFactors: number
): ScoreResult => {
  if (totalStrongFactors === 0 && totalWeakFactors === 0) {
    return {
      score: -1,
      level: 'Not Analyzed',
      color: 'bg-gray-100 text-gray-600',
      description: 'Analysis not completed',
    };
  }

  // Calculate percentage of strong and weak factors checked
  const strongPercentage = totalStrongFactors > 0 ? strongFactorsChecked / totalStrongFactors : 0;
  const weakPercentage = totalWeakFactors > 0 ? weakFactorsChecked / totalWeakFactors : 0;

  // Start at 50% (moderate) and adjust based on factors
  // Strong factors decrease the score (bad for industry attractiveness)
  // Weak factors increase the score (good for industry attractiveness)
  const score = 50 + weakPercentage * 50 - strongPercentage * 50;
  const clampedScore = Math.round(Math.max(0, Math.min(100, score)));

  // Convert score to pressure level
  if (clampedScore >= 80) {
    return {
      score: clampedScore,
      level: 'Very Weak',
      color: 'bg-blue-100 text-blue-800',
      description: 'Very favorable competitive force',
    };
  } else if (clampedScore >= 60) {
    return {
      score: clampedScore,
      level: 'Weak',
      color: 'bg-green-100 text-green-800',
      description: 'Favorable competitive force',
    };
  } else if (clampedScore >= 40) {
    return {
      score: clampedScore,
      level: 'Moderate',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Moderate competitive pressure',
    };
  } else if (clampedScore >= 20) {
    return {
      score: clampedScore,
      level: 'Strong',
      color: 'bg-orange-100 text-orange-800',
      description: 'Strong competitive pressure',
    };
  } else {
    return {
      score: clampedScore,
      level: 'Very Strong',
      color: 'bg-red-100 text-red-800',
      description: 'Very strong competitive pressure',
    };
  }
};

/**
 * Calculate industry attractiveness from Five Forces average
 */
export const calculateIndustryAttractiveness = (forceScores: number[]): ScoreResult => {
  const validScores = forceScores.filter((score) => score >= 0);

  if (validScores.length === 0) {
    return {
      score: -1,
      level: 'Not Analyzed',
      color: 'bg-gray-100 text-gray-600',
      description: 'Five Forces analysis not completed',
    };
  }

  const averageScore = validScores.reduce((sum, score) => sum + score, 0) / validScores.length;

  if (averageScore >= 80) {
    return {
      score: Math.round(averageScore),
      level: 'Very High',
      color: 'bg-blue-100 text-blue-800',
      description: 'Highly attractive industry',
    };
  } else if (averageScore >= 60) {
    return {
      score: Math.round(averageScore),
      level: 'High',
      color: 'bg-green-100 text-green-800',
      description: 'Attractive industry',
    };
  } else if (averageScore >= 40) {
    return {
      score: Math.round(averageScore),
      level: 'Moderate',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Moderately attractive industry',
    };
  } else if (averageScore >= 20) {
    return {
      score: Math.round(averageScore),
      level: 'Low',
      color: 'bg-orange-100 text-orange-800',
      description: 'Low attractiveness industry',
    };
  } else {
    return {
      score: Math.round(averageScore),
      level: 'Very Low',
      color: 'bg-red-100 text-red-800',
      description: 'Unattractive industry',
    };
  }
};

/**
 * Calculate driving forces volatility
 * Based on absolute count of factors that are changing
 */
export const calculateDrivingForcesVolatility = (
  checkedFactors: number,
  totalFactors: number,
  doesNotApply: boolean = false
): ScoreResult => {
  if (doesNotApply) {
    return {
      score: 0,
      level: 'Very Low',
      color: 'bg-blue-100 text-blue-800',
      description: 'This driving force does not apply to the industry',
    };
  }

  if (totalFactors === 0) {
    return {
      score: -1,
      level: 'Not Analyzed',
      color: 'bg-gray-100 text-gray-600',
      description: 'Analysis not completed',
    };
  }

  // Use absolute count: 1=Moderate, 2=High, 3+=Very High
  if (checkedFactors >= 3) {
    return {
      score: 100,
      level: 'Very High',
      color: 'bg-red-100 text-red-800',
      description: `Very high volatility (${checkedFactors}/${totalFactors} factors changing)`,
    };
  } else if (checkedFactors === 2) {
    return {
      score: 66,
      level: 'High',
      color: 'bg-orange-100 text-orange-800',
      description: `High volatility (${checkedFactors}/${totalFactors} factors changing)`,
    };
  } else if (checkedFactors === 1) {
    return {
      score: 33,
      level: 'Moderate',
      color: 'bg-yellow-100 text-yellow-800',
      description: `Moderate volatility (${checkedFactors}/${totalFactors} factors changing)`,
    };
  } else {
    return {
      score: 0,
      level: 'Very Low',
      color: 'bg-blue-100 text-blue-800',
      description: 'Very stable - no significant changes detected',
    };
  }
};

/**
 * Calculate overall external challenge from PESTEL analysis
 */
export const calculateExternalChallenge = (pestelScores: number[]): ScoreResult => {
  const validScores = pestelScores.filter((score) => score >= 0);

  if (validScores.length === 0) {
    return {
      score: -1,
      level: 'Not Analyzed',
      color: 'bg-gray-100 text-gray-600',
      description: 'PESTEL analysis not completed',
    };
  }

  const averageScore = validScores.reduce((sum, score) => sum + score, 0) / validScores.length;

  // Reverse logic: High opportunities = Low challenge
  if (averageScore >= 80) {
    return {
      score: Math.round(averageScore),
      level: 'Very Low',
      color: 'bg-blue-100 text-blue-800',
      description: 'Very low external challenge',
    };
  } else if (averageScore >= 60) {
    return {
      score: Math.round(averageScore),
      level: 'Low',
      color: 'bg-green-100 text-green-800',
      description: 'Low external challenge',
    };
  } else if (averageScore >= 40) {
    return {
      score: Math.round(averageScore),
      level: 'Moderate',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Moderate external challenge',
    };
  } else if (averageScore >= 20) {
    return {
      score: Math.round(averageScore),
      level: 'High',
      color: 'bg-orange-100 text-orange-800',
      description: 'High external challenge',
    };
  } else {
    return {
      score: Math.round(averageScore),
      level: 'Very High',
      color: 'bg-red-100 text-red-800',
      description: 'Very high external challenge',
    };
  }
};

// =============================================================================
// INTERNAL ASSESSMENT SCORING
// =============================================================================

/**
 * Calculate SWOT analysis completion and balance
 */
export const calculateSwotScore = (
  strengths: number,
  weaknesses: number,
  opportunities: number,
  threats: number
): ScoreResult => {
  const total = strengths + weaknesses + opportunities + threats;

  if (total === 0) {
    return {
      score: 0,
      level: 'Not Started',
      color: 'bg-gray-100 text-gray-600',
      description: 'SWOT analysis not started',
    };
  }

  // Check for balance across quadrants
  const hasAllQuadrants = strengths > 0 && weaknesses > 0 && opportunities > 0 && threats > 0;
  const completionScore = hasAllQuadrants ? 100 : Math.round((total / 20) * 100); // Assume 5 items per quadrant as target

  if (completionScore >= 80 && hasAllQuadrants) {
    return {
      score: completionScore,
      level: 'Comprehensive',
      color: 'bg-green-100 text-green-800',
      description: 'Well-balanced SWOT analysis',
    };
  } else if (completionScore >= 60) {
    return {
      score: completionScore,
      level: 'Good',
      color: 'bg-blue-100 text-blue-800',
      description: 'Solid SWOT analysis',
    };
  } else if (completionScore >= 40) {
    return {
      score: completionScore,
      level: 'Partial',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Incomplete SWOT analysis',
    };
  } else {
    return {
      score: completionScore,
      level: 'Minimal',
      color: 'bg-orange-100 text-orange-800',
      description: 'Limited SWOT analysis',
    };
  }
};

/**
 * Calculate industry success factors alignment
 */
export const calculateSuccessFactorsAlignment = (
  strongFactors: number,
  moderateFactors: number,
  weakFactors: number
): ScoreResult => {
  const total = strongFactors + moderateFactors + weakFactors;

  if (total === 0) {
    return {
      score: 0,
      level: 'Not Assessed',
      color: 'bg-gray-100 text-gray-600',
      description: 'Success factors not assessed',
    };
  }

  // Weight: Strong = 100%, Moderate = 60%, Weak = 20%
  const weightedScore = Math.round(
    ((strongFactors * 100 + moderateFactors * 60 + weakFactors * 20) / (total * 100)) * 100
  );

  if (weightedScore >= 80) {
    return {
      score: weightedScore,
      level: 'Excellent',
      color: 'bg-green-100 text-green-800',
      description: 'Strong alignment with success factors',
    };
  } else if (weightedScore >= 60) {
    return {
      score: weightedScore,
      level: 'Good',
      color: 'bg-blue-100 text-blue-800',
      description: 'Good alignment with success factors',
    };
  } else if (weightedScore >= 40) {
    return {
      score: weightedScore,
      level: 'Moderate',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Moderate alignment with success factors',
    };
  } else {
    return {
      score: weightedScore,
      level: 'Weak',
      color: 'bg-red-100 text-red-800',
      description: 'Weak alignment with success factors',
    };
  }
};

// =============================================================================
// STRATEGY FORMULATION SCORING
// =============================================================================

/**
 * Calculate strategy selection and implementation readiness
 */
export const calculateStrategyReadiness = (
  selectedStrategies: number,
  totalObjectives: number,
  implementationPlan: boolean
): ScoreResult => {
  if (totalObjectives === 0) {
    return {
      score: 0,
      level: 'Not Started',
      color: 'bg-gray-100 text-gray-600',
      description: 'Strategy formulation not started',
    };
  }

  const selectionScore = Math.round((selectedStrategies / totalObjectives) * 70); // 70% weight
  const planScore = implementationPlan ? 30 : 0; // 30% weight
  const totalScore = selectionScore + planScore;

  if (totalScore >= 90) {
    return {
      score: totalScore,
      level: 'Ready',
      color: 'bg-green-100 text-green-800',
      description: 'Strategy ready for implementation',
    };
  } else if (totalScore >= 70) {
    return {
      score: totalScore,
      level: 'Nearly Ready',
      color: 'bg-blue-100 text-blue-800',
      description: 'Strategy nearly ready for implementation',
    };
  } else if (totalScore >= 40) {
    return {
      score: totalScore,
      level: 'In Progress',
      color: 'bg-yellow-100 text-yellow-800',
      description: 'Strategy formulation in progress',
    };
  } else {
    return {
      score: totalScore,
      level: 'Early Stage',
      color: 'bg-orange-100 text-orange-800',
      description: 'Strategy formulation in early stages',
    };
  }
};

// =============================================================================
// OVERALL PROGRESS CALCULATION
// =============================================================================

/**
 * Calculate overall strategic planning progress
 */
export const calculateOverallProgress = (
  foundationProgress: number,
  externalProgress: number,
  internalProgress: number,
  formulationProgress: number
): ProgressResult => {
  const phases = [
    { name: 'Foundation', progress: foundationProgress, weight: 0.25 },
    { name: 'External Assessment', progress: externalProgress, weight: 0.35 },
    { name: 'Internal Assessment', progress: internalProgress, weight: 0.25 },
    { name: 'Strategy Formulation', progress: formulationProgress, weight: 0.15 },
  ];

  const weightedProgress = phases.reduce((total, phase) => {
    return total + phase.progress * phase.weight;
  }, 0);

  const overallPercentage = Math.round(weightedProgress);

  return {
    completed: phases.filter((p) => p.progress >= 100).length,
    total: phases.length,
    percentage: overallPercentage,
    status:
      overallPercentage >= 100
        ? 'completed'
        : overallPercentage > 0
          ? 'in_progress'
          : 'not_started',
  };
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get status color based on progress percentage
 */
export const getProgressColor = (percentage: number): string => {
  if (percentage >= 100) return 'bg-green-100 text-green-800';
  if (percentage >= 75) return 'bg-blue-100 text-blue-800';
  if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
  if (percentage >= 25) return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-600';
};

/**
 * Get trend icon based on direction
 */
export const getTrendDirection = (direction: string): { icon: string; color: string } => {
  switch (direction.toLowerCase()) {
    case 'increasing':
    case 'up':
    case 'high_change':
      return { icon: 'ArrowUp', color: 'text-red-600' };
    case 'decreasing':
    case 'down':
      return { icon: 'ArrowDown', color: 'text-blue-600' };
    case 'stable':
    case 'neutral':
      return { icon: 'Minus', color: 'text-green-600' };
    case 'moderate_change':
      return { icon: 'TrendingUp', color: 'text-yellow-600' };
    case 'low_change':
      return { icon: 'TrendingDown', color: 'text-orange-600' };
    case 'not_applicable':
      return { icon: 'Minus', color: 'text-gray-400' };
    default:
      return { icon: 'AlertTriangle', color: 'text-gray-500' };
  }
};

/**
 * Validate score result
 */
export const isValidScore = (result: ScoreResult): boolean => {
  return result.score >= -1 && result.level !== '' && result.color !== '';
};
